import React, { useState, useContext } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Alert 
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { useAuth } from '../context/authContext';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { useUrlContext } from '../context/urlContext';
import { errorContext } from '../Components/ErrorDisplay';
import { messageForStatus } from '../Components/errorUtils';
import { landingPathForRole } from '../Components/roles';


const validationSchema = yup.object({
  username: yup
    .string('Enter your username')
    .required('Username is required'),
  password: yup
    .string('Enter your password')
    .min(6, 'Password should be at least 6 characters')
    .required('Password is required'),
});

// LoginPage.js

const LoginPage = () => {
    const { UrlHelperObj } = useUrlContext();
  const { login, isAuthenticated } = useAuth();
  const { notify } = useContext(errorContext);
  const history = useHistory();
  const location = useLocation();

  if (isAuthenticated) {
    return <Redirect to="/" />;
  }

  const handleSubmit = async (values) => {
    // 1) Network/connection problem: fetch only throws when the server can't be
    //    reached at all. This must NOT be reported as a wrong-password error.
    let response;
    try {
      response = await fetch(UrlHelperObj.validateUserUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values)
      });
    } catch (networkErr) {
      console.error('Login network error', networkErr);
      notify('Cannot reach the server. Please check your internet connection and try again.', 'error');
      return;
    }

    // Parse the body defensively (it may be empty or non-JSON on errors).
    let data = null;
    try { data = await response.json(); } catch (e) { data = null; }

    // 2) Server reachable but returned an error status (e.g. 5xx, 4xx).
    if (!response.ok) {
      notify(messageForStatus(response.status, data && data.message), 'error');
      return;
    }

    // 3) App-level auth failure: this backend returns HTTP 200 with { error: 1 }.
    if (data && data.error === 1) {
      notify(data.message || 'Invalid username or password.', 'error');
      return;
    }

    // 4) Unexpected success shape.
    if (!data || !data.user || !data.token) {
      notify('Unexpected response from the server. Please try again.', 'error');
      return;
    }

    // 5) Success.
    login(data.user, data.entry, data.token);
    const role = Number(data.user?.role);
    const issuerId = data.user?.issuerId ?? data.user?.issuer_id;
    const defaultPath = landingPathForRole(role, issuerId);
    const dest = (location.state && location.state.from) ? location.state.from : { pathname: defaultPath };
    history.replace(dest);
  };

  // ... rest of your Formik form


  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Sign In
          </Typography>
          <Formik
            initialValues={{ username: '', password: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
              <Form>
                {errors.general && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.general}
                  </Alert>
                )}
                
                <TextField
                  margin="normal"
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={values.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.username && Boolean(errors.username)}
                  helperText={touched.username && errors.username}
                />
                
                <TextField
                  margin="normal"
                  fullWidth
                  id="password"
                  label="Password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                />
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Signing In...' : 'Sign In'}
                </Button>
              </Form>
            )}
          </Formik>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
